/* A single real search input is shared by the header and mobile search dialog. */
(() => {
  'use strict';
  let active = null;
  function init() {
    const header = document.querySelector('[data-yoko-header]');
    if (!header) return;
    const slot = header.querySelector('.yoko-search-slot');
    const drawer = document.querySelector('.yoko-search-drawer');
    const wrapper = header.closest('.header-wrap');
    if (!slot || !drawer || !wrapper) return;
    if (active?.header === header && active.drawer === drawer) return;
    active?.dispose();
    const form = slot.querySelector('form');
    const input = form?.querySelector('input[name="q"]');
    const mount = drawer.querySelector('.yoko-search-mount');
    const closeButton = drawer.querySelector('.close-search');
    const clearButton = form?.querySelector('.yoko-search-clear');
    const backdrop = document.querySelector('.yoko-search-backdrop');
    if (!form || !input || !mount || !closeButton || !backdrop) return;
    const cleanups = [];
    function listen(target, name, callback, options) {
      target.addEventListener(name, callback, options);
      cleanups.push(() => target.removeEventListener(name, callback, options));
    }
    header.dataset.yokoReady = 'true';
    // Use the same server-rendered cart response as the item count: no extra request,
    // and currency/discount formatting stays identical to the drawer subtotal.
    const cartBubble = header.querySelector('#cart-icon-bubble');
    const cartTotal = header.querySelector('[data-yoko-cart-total-display]');
    if (cartBubble && cartTotal) {
      const syncCartTotal = () => {
        const state = cartBubble.querySelector('[data-yoko-cart-total]');
        if (!state) return;
        cartTotal.querySelector('.yoko-cart-total-desktop').textContent = state.dataset.yokoCartTotal;
        cartTotal.querySelector('.yoko-cart-total-mobile').textContent = state.dataset.yokoCartTotalMobile || state.dataset.yokoCartTotal;
        cartTotal.hidden = Number(state.dataset.yokoCartCount) === 0;
      };
      const cartObserver = new MutationObserver(syncCartTotal);
      cartObserver.observe(cartBubble, { childList:true, subtree:true });
      cleanups.push(() => cartObserver.disconnect());
      syncCartTotal();
    }
    const root = document.documentElement;
    const mobile = window.matchMedia('(max-width:1024px)');
    let opened = false;
    let previousY = window.scrollY;
    let headerBottom = 0;
    function measureHeader() {
      if (opened) return;
      const compact = wrapper.classList.contains('yoko-compact');
      wrapper.classList.remove('yoko-compact');
      wrapper.style.setProperty('--yoko-full-header-height', `${header.offsetHeight}px`);
      wrapper.classList.toggle('yoko-compact', compact);
      if (!wrapper.classList.contains('yoko-mobile-sticky')) headerBottom = wrapper.getBoundingClientRect().bottom + window.scrollY;
    }
    measureHeader();
    listen(window, 'load', measureHeader, { once:true });
    let scrollFrame = 0;
    function updateScroll() {
      scrollFrame = 0;
      if (opened && !mobile.matches) { positionDrawer(); return; }
      if (!mobile.matches || opened || root.classList.contains('show-overly') || header.dataset.sticky === 'none') return;
      const y = Math.max(0, window.scrollY);
      if (Math.abs(y - previousY) < 5 && y > headerBottom) return;
      const past = y > headerBottom;
      wrapper.classList.toggle('yoko-mobile-sticky', past);
      wrapper.classList.toggle('yoko-compact', past && y > previousY);
      previousY = y;
    }
    listen(window, 'scroll', () => {
      if (!scrollFrame) scrollFrame = requestAnimationFrame(updateScroll);
    }, { passive:true });

    const results = drawer.querySelector('#serch-list');
    const suggestions = drawer.querySelector('#search-suggest__Wrapper');
    const triggers = [...header.querySelectorAll('.searchIcn')];
    const viewport = window.visualViewport;
    let returnTarget = input;
    let savedY = 0;
    let savedBody = null;
    let restoringFocus = false;
    let timer;
    let controller;
    let requestNumber = 0;
    function syncClearButton() {
      if (clearButton) clearButton.hidden = input.value.length === 0;
    }
    syncClearButton();
    input.setAttribute('aria-controls', drawer.id);
    // A native searchbox does not support aria-expanded; disclosure buttons own that state.
    input.removeAttribute('aria-expanded');
    input.setAttribute('aria-haspopup', 'dialog');
    triggers.forEach(el => {
      el.setAttribute('aria-controls', drawer.id);
      el.setAttribute('aria-expanded', 'false');
    });
    function positionDrawer() {
      const height = viewport ? viewport.height : window.innerHeight;
      drawer.style.setProperty('--yoko-viewport-height', `${height}px`);
      drawer.style.setProperty('--yoko-viewport-top', `${viewport ? viewport.offsetTop : 0}px`);
      if (!mobile.matches) {
        const bounds = form.getBoundingClientRect();
        drawer.style.setProperty('--yoko-search-top', `${Math.max(0, bounds.bottom) + 8}px`);
        drawer.style.setProperty('--yoko-search-left', `${bounds.left}px`);
        drawer.style.setProperty('--yoko-search-width', `${bounds.width}px`);
      }
    }
    function moveForm() {
      const destination = mobile.matches ? mount : slot;
      if (form.parentElement !== destination) destination.appendChild(form);
    }
    function openSearch(target) {
      if (opened || restoringFocus) return;
      opened = true;
      returnTarget = target || input;
      savedY = window.scrollY;
      wrapper.classList.remove('header-hidden');
      if (mobile.matches) {
        savedBody = ['position','top','width'].map(key => [key, document.body.style[key]]);
        document.body.style.position = 'fixed';
        document.body.style.top = `-${savedY}px`;
        document.body.style.width = '100%';
        drawer.setAttribute('aria-modal','true');
      }
      root.classList.add('searchact');
      drawer.inert = false;
      drawer.setAttribute('aria-hidden','false');
      backdrop.hidden = false;
      triggers.forEach(el => el.setAttribute('aria-expanded','true'));
      moveForm();
      positionDrawer();
      input.focus({ preventScroll:true });
      queueSearch();
    }
    function closeSearch(restore = true) {
      if (!opened) return;
      opened = false;
      clearTimeout(timer);
      if (controller) controller.abort();
      requestNumber++;
      input.blur();
      if (form.parentElement !== slot) slot.appendChild(form);
      root.classList.remove('searchact');
      drawer.inert = true;
      drawer.setAttribute('aria-hidden','true');
      drawer.removeAttribute('aria-modal');
      backdrop.hidden = true;
      triggers.forEach(el => el.setAttribute('aria-expanded','false'));
      if (savedBody) {
        savedBody.forEach(([key,value]) => { document.body.style[key] = value; });
        savedBody = null;
        window.scrollTo({ top:savedY, left:0, behavior:'instant' });
      }
      if (restore) {
        restoringFocus = true;
        // Returning focus to a text input on mobile would reopen the keyboard.
        const preferred = mobile.matches && returnTarget === input ? header.querySelector('.nav_toggle') : returnTarget;
        const target = preferred?.getClientRects().length ? preferred : header.querySelector('.nav_toggle');
        if (target) target.focus({ preventScroll:true });
        restoringFocus = false;
      }
    }
    function showSuggestions() {
      if (results) {
        results.replaceChildren();
        results.classList.add('hide');
        results.removeAttribute('aria-busy');
        results.style.removeProperty('min-height');
        results.inert = false;
      }
      if (suggestions) suggestions.classList.remove('hide');
    }
    function queueSearch() {
      clearTimeout(timer);
      if (controller) controller.abort();
      const number = ++requestNumber;
      const term = input.value.trim();
      syncClearButton();
      if (!results || term.length < 3) { showSuggestions(); return; }
      // Keep the current panel steady while typing. Old matches stay visible but
      // cannot be selected until the newest response replaces them.
      const previousHeight = results.getBoundingClientRect().height;
      if (suggestions) suggestions.classList.add('hide');
      results.style.minHeight = `${Math.max(120, previousHeight)}px`;
      results.classList.remove('hide');
      results.inert = true;
      results.setAttribute('aria-busy','true');
      timer = setTimeout(async () => {
        controller = new AbortController();
        try {
          const url = new URL(window.routes?.predictive_search_url || `${form.action.replace(/\/$/,'')}/suggest`, window.location.origin);
          url.searchParams.set('q',term);
          url.searchParams.set('section_id','predictive-search');
          const response = await fetch(url, { signal:controller.signal });
          if (!response.ok) throw new Error(`Search ${response.status}`);
          const text = await response.text();
          if (!opened || number !== requestNumber) return;
          const content = new DOMParser().parseFromString(text,'text/html').querySelector('#shopify-section-predictive-search');
          if (!content) throw new Error('Missing search section');
          content.querySelectorAll('a[href]').forEach(link => {
            const destination = new URL(link.getAttribute('href'), window.location.origin);
            const searchPage = new URL(form.action, window.location.origin);
            if (link.classList.contains('btn') && destination.origin === searchPage.origin && destination.pathname === searchPage.pathname && destination.searchParams.has('q')) {
              destination.searchParams.set('q', term);
              link.setAttribute('href', destination.pathname + destination.search);
            }
          });
          results.innerHTML = content.innerHTML;
          // Existing predictive templates contain negative tabindex on ordinary links.
          results.querySelectorAll('a[tabindex="-1"]').forEach(el => el.removeAttribute('tabindex'));
          results.classList.remove('hide');
          results.removeAttribute('aria-busy');
          results.style.removeProperty('min-height');
          results.inert = false;
          if (suggestions) suggestions.classList.add('hide');
          if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger();
        } catch (error) {
          if (error.name !== 'AbortError' && number === requestNumber) showSuggestions();
          // The regular GET search form remains usable even when suggestions fail.
        }
      }, 180);
    }
    // Do not move a focused input between containers in the middle of a tap.
    // On mobile Chromium the compatibility mouse events can lose their target
    // and the trailing click can immediately close the dialog. Let click finish
    // the gesture first; openSearch then moves and focuses the input synchronously.
    listen(input, 'pointerdown', event => {
      if (mobile.matches && !opened && event.isPrimary && event.button === 0) {
        event.preventDefault();
      }
    });
    listen(input, 'focus', () => openSearch(input));
    listen(input, 'click', () => openSearch(input));
    listen(input, 'input', () => { if (!opened) openSearch(input); else queueSearch(); });
    if (clearButton) {
      // Keep the mobile keyboard open when clearing with a pointer.
      listen(clearButton, 'pointerdown', event => event.preventDefault());
      listen(clearButton, 'click', () => {
        input.value = '';
        input.focus({ preventScroll:true });
        queueSearch();
      });
    }
    triggers.forEach(el => listen(el, 'click', e => { e.preventDefault(); openSearch(el); }));
    listen(closeButton, 'click', () => closeSearch());
    listen(backdrop, 'click', () => closeSearch());
    listen(document, 'click', e => {
      if (opened && !drawer.contains(e.target) && !slot.contains(e.target) && !e.target.closest('.searchIcn')) closeSearch(false);
    });
    listen(document, 'keydown', e => {
      if (!opened) return;
      if (e.key === 'Escape') { e.preventDefault(); closeSearch(); return; }
      if (!mobile.matches) {
        if (e.key === 'Tab' && !e.shiftKey && document.activeElement === form.querySelector('button[type="submit"]')) {
          e.preventDefault(); closeButton.focus();
        }
        return;
      }
      if (e.key !== 'Tab') return;
      const focusable = [...drawer.querySelectorAll('a[href],button:not([disabled]),input:not([disabled]),[tabindex="0"]')]
        .filter(el => el.getClientRects().length && !el.closest('[inert]'));
      const first = focusable[0], last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });
    listen(document, 'focusin', e => {
      if (opened && !mobile.matches && !drawer.contains(e.target) && !slot.contains(e.target)) closeSearch(false);
    });
    listen(form, 'submit', event => {
      if (!input.value.trim()) {
        event.preventDefault();
        openSearch(input);
        input.focus({ preventScroll:true });
        return;
      }
      // Leave q intact until the browser has submitted the GET form.
      closeSearch(false);
    });
    listen(window, 'pageshow', event => {
      // Initial pageshow follows load: a shopper may already have opened search
      // while images/apps are still loading. Only reset a restored bfcache page.
      if (event.persisted) closeSearch(false);
      if (event.persisted && form.hasAttribute('data-yoko-fresh-search') && document.body.classList.contains('template-search')) {
        input.value = '';
        showSuggestions();
      }
      syncClearButton();
    });
    listen(mobile, 'change', () => {
      closeSearch(false);
      wrapper.classList.remove('yoko-mobile-sticky','yoko-compact','sticky-header','header-hidden');
      measureHeader();
      previousY = window.scrollY;
    });
    // On iOS Chrome the browser toolbar changes the viewport height as it
    // collapses/expands. Re-measuring the compact header on every such resize
    // temporarily removes its class and causes a visible jump.
    let lastViewportWidth = window.innerWidth;
    listen(window, 'resize', () => {
      if (opened) positionDrawer();
      const nextWidth = window.innerWidth;
      if (nextWidth !== lastViewportWidth) {
        lastViewportWidth = nextWidth;
        measureHeader();
      }
    });
    // Cart totals and translated labels can resize the search field independently
    // of the viewport. Keep both dropdown edges anchored to the actual form.
    if (window.ResizeObserver) {
      const resizeObserver = new window.ResizeObserver(() => {
        if (opened && !mobile.matches) positionDrawer();
      });
      resizeObserver.observe(slot);
      cleanups.push(() => resizeObserver.disconnect());
    }
    if (viewport) {
      listen(viewport, 'resize', positionDrawer, { passive:true });
      listen(viewport, 'scroll', positionDrawer, { passive:true });
    }
    active = { header, drawer, dispose() {
      closeSearch(false);
      clearTimeout(timer);
      controller?.abort();
      requestNumber++;
      cancelAnimationFrame(scrollFrame);
      cleanups.forEach(cleanup => cleanup());
      wrapper.classList.remove('yoko-mobile-sticky', 'yoko-compact');
      wrapper.style.removeProperty('--yoko-full-header-height');
      delete header.dataset.yokoReady;
      active = null;
    }};
  }
  document.addEventListener('shopify:section:unload', event => {
    if (active && (event.target.contains(active.header) || event.target.contains(active.drawer))) active.dispose();
  });
  document.addEventListener('shopify:section:load', () => {
    if (active && (!active.header.isConnected || !active.drawer.isConnected)) active.dispose();
    init();
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once:true });
  else init();
})();
